import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { _js, ___p } from '@ifixit/utils/localize';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import {
   Pagination,
   PaginationContent,
   PaginationItem,
   PaginationLink,
   PaginationNext,
   PaginationPrevious,
   calculatePageRange,
} from '@ifixit/ui/components/pagination';
import { Skeleton } from '@ifixit/ui/components/skeleton';
import { cn } from '@ifixit/ui/lib/utils';
import {
   type KeyboardEvent as ReactKeyboardEvent,
   type MouseEvent as ReactMouseEvent,
   useEffect,
   useRef,
   useState,
} from 'react';
import { COLUMN_LABEL_ON_FILL_CLASS } from 'Shared/editor/field';
import { MessageCard } from 'Shared/editor/message_card';
import type {
   LeaderboardBadge,
   LeaderboardPageData,
   LeaderboardRow,
   LeaderboardProps,
   TimeFilter,
} from './leaderboard_types';
import { BadgeWallBanner } from './BadgeWallBanner';
import { useLeaderboardPage } from './use_leaderboard_api';

/**
 * The community leaderboard: members ranked by the reputation they earned
 * in a time period. The shell, card, and list grammar mirror the
 * /Translate quality dashboard — the sibling tab in the community nav —
 * so the two pages read as one surface.
 */

/** The list container, in the editors' SectionCard weight: gray-300
 *  border, full-bleed with square corners below md. */
const BOARD_CARD_CLASS =
   'overflow-hidden rounded-2xl border border-gray-300 bg-white max-md:-mx-4 max-md:rounded-none max-md:border-x-0';

/** The desktop column template the rows and headers share: rank, member,
 *  recent badges, right-aligned reputation. Below md the badge column
 *  hides and rows stay a single flex line — a ranking is a scan list,
 *  so it never stacks. */
const BOARD_GRID_CLASS =
   'md:grid md:grid-cols-[3.5rem_minmax(0,1fr)_10.5rem_6.5rem] md:items-center md:gap-x-3';

/** v1's event names, untranslated, so the analytics series continues
 *  across the redesign. */
const FILTER_ANALYTICS_NAMES: Record<TimeFilter, string> = {
   today: 'Today',
   week: 'Week',
   month: 'Month',
   'all-time': 'All Time',
};

export function LeaderboardDashboard(props: LeaderboardProps) {
   const filterKeys = props.filters.map(filter => filter.key);
   // The server parsed the same URL, resolved the adaptive default
   // period, and clamped an out-of-range page, so its payload is the
   // initial view. Parsing the raw URL here instead would key the cache
   // on e.g. page 99 and skip the seed the server just rendered.
   const [view, setView] = useBoardView(filterKeys, {
      filter: props.initialData.filter,
      page: props.initialData.page,
   });
   const boardRef = useRef<HTMLElement | null>(null);
   const query = useLeaderboardPage(view.filter, view.page, props.initialData);

   const changePage = (nextPage: number) => {
      setView({ filter: view.filter, page: nextPage });
      boardRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
   };

   const pageData = query.data;

   return (
      <div className="mx-auto w-full max-w-5xl px-4 pb-10 md:px-8">
         {/* The community nav above renders tabs-only for this page; the
             H1, join button, and help link all live here so the whole
             header is one component. */}
         <header className="pt-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
               <h1 className="text-[30px] font-medium text-gray-950">{_js('Leaderboard')}</h1>
               {!props.isLoggedIn && (
                  <a
                     href="/Join"
                     className="button-primary button-md no-underline hover:no-underline"
                  >
                     {_js('Join the community')}
                  </a>
               )}
            </div>
            <p className="mt-2 max-w-2xl text-sm text-gray-700">
               {_js(
                  'The people making repair easier for everyone, ranked by the reputation their contributions earned.'
               )}{' '}
               <a
                  href={props.helpUrl}
                  className="whitespace-nowrap font-medium text-brand-500 underline-offset-2 hover:text-brand-600 hover:underline"
               >
                  {_js('How reputation works')}
               </a>
            </p>
         </header>

         <BadgeWallBanner
            banner={props.banner}
            isLoggedIn={props.isLoggedIn}
            badgeIndexUrl={props.badgeIndexUrl}
         />

         <div className="mt-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <TimeFilterTabs
               filters={props.filters}
               activeFilter={view.filter}
               onSelect={filter => {
                  trackAnalyticsCustomEvent({
                     eventCategory: 'Leaderboard',
                     eventAction: `Leaderboard - Filter - ${FILTER_ANALYTICS_NAMES[filter]}`,
                  });
                  setView({ filter, page: 1 });
               }}
            />
            <DateRange filter={view.filter} />
         </div>

         <section
            ref={boardRef}
            id="leaderboard-panel"
            role="tabpanel"
            aria-labelledby={`leaderboard-filter-${view.filter}`}
            className="mt-4 scroll-mt-4"
         >
            <div className={cn(BOARD_CARD_CLASS, query.isPlaceholderData && 'opacity-60')}>
               {pageData ? (
                  pageData.rows.length === 0 ? (
                     <MessageCard
                        icon="trophy"
                        tone="info"
                        title={_js('No one is on the leaderboard yet.')}
                        text={_js('Sounds like an opportunity!')}
                     />
                  ) : (
                     <>
                        <BoardColumnHeaders />
                        <ul className="my-0 list-none divide-y divide-gray-200 pl-0">
                           {pageData.rows.map((row, index) => (
                              <BoardRow
                                 key={row.userid}
                                 row={row}
                                 rank={(pageData.page - 1) * pageData.perPage + index + 1}
                              />
                           ))}
                        </ul>
                     </>
                  )
               ) : query.isError ? (
                  <MessageCard
                     icon="triangle-exclamation"
                     tone="failure"
                     title={_js('The leaderboard didn’t load')}
                     text={_js('Something went wrong on our side. Reload to try again.')}
                  />
               ) : (
                  <BoardSkeleton />
               )}
            </div>
            {pageData && (
               <BoardPagination
                  page={view.page}
                  filter={view.filter}
                  pageData={pageData}
                  onPageChange={changePage}
               />
            )}
         </section>
      </div>
   );
}

/**
 * The time-period switcher as a segmented single-select (DESIGN.md:
 * brand-50 fill on the active option) — the options are an either/or
 * over the same list, not sections of different content, so they don't
 * take the tab-strip pill weight /Translate's queues have.
 */
export function TimeFilterTabs({
   filters,
   activeFilter,
   onSelect,
}: {
   filters: LeaderboardProps['filters'];
   activeFilter: TimeFilter;
   onSelect: (filter: TimeFilter) => void;
}) {
   const tabsRef = useRef<Array<HTMLAnchorElement | null>>([]);
   // Manual activation: arrows only move focus (selecting costs a fetch,
   // an analytics event, and a history entry, so a traversal must be
   // free); Enter or Space commits. The roving tabindex follows the
   // focused tab and falls back to the active one when focus is outside
   // the strip.
   const [focusedFilter, setFocusedFilter] = useState<TimeFilter | null>(null);

   const select = (key: TimeFilter) => {
      // Reselecting the active period would re-fire analytics, push a
      // duplicate history entry, and yank someone on page 4 back to
      // page 1. v1 had the same guard.
      if (key !== activeFilter) {
         onSelect(key);
      }
   };

   const onTabKeyDown = (event: ReactKeyboardEvent<HTMLAnchorElement>, index: number) => {
      if (event.key === ' ') {
         // Space activates a button but not a link; a tab expects it.
         event.preventDefault();
         select(filters[index].key);
         return;
      }
      const nextIndex = {
         ArrowRight: index + 1,
         ArrowDown: index + 1,
         ArrowLeft: index - 1,
         ArrowUp: index - 1,
         Home: 0,
         End: filters.length - 1,
      }[event.key];
      if (nextIndex !== undefined) {
         event.preventDefault();
         tabsRef.current[(nextIndex + filters.length) % filters.length]?.focus();
      }
   };

   const filterHref = (key: TimeFilter) => {
      const url = new URL(window.location.href);
      url.searchParams.set('filter', key);
      url.searchParams.delete('page');
      return url.pathname + url.search;
   };

   return (
      <div
         role="tablist"
         aria-label={_js('Time period')}
         className="flex flex-wrap gap-2"
         onBlur={event => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
               setFocusedFilter(null);
            }
         }}
      >
         {filters.map((filterOption, index) => {
            const isActive = filterOption.key === activeFilter;
            return (
               <a
                  key={filterOption.key}
                  ref={element => {
                     tabsRef.current[index] = element;
                  }}
                  href={filterHref(filterOption.key)}
                  role="tab"
                  id={`leaderboard-filter-${filterOption.key}`}
                  aria-selected={isActive}
                  aria-controls="leaderboard-panel"
                  tabIndex={(focusedFilter ?? activeFilter) === filterOption.key ? 0 : -1}
                  onFocus={() => setFocusedFilter(filterOption.key)}
                  onKeyDown={event => onTabKeyDown(event, index)}
                  onClick={event => {
                     // Modified clicks keep the native link behavior
                     // (new tab/window) via the real href.
                     if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                        return;
                     }
                     event.preventDefault();
                     select(filterOption.key);
                  }}
                  className={cn(
                     'rounded-lg border px-3 py-1.5 text-sm font-medium no-underline transition-colors hover:no-underline',
                     'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                     isActive
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  )}
               >
                  {filterOption.label}
               </a>
            );
         })}
      </div>
   );
}

/**
 * The dates the ranking covers, so "Week" isn't left to interpretation.
 * All time has no range to name and renders nothing.
 */
function DateRange({ filter }: { filter: TimeFilter }) {
   const label = dateRangeLabel(filter);
   if (label === null) {
      return null;
   }
   return <p className="my-0 text-xs text-gray-500">{label}</p>;
}

function dateRangeLabel(filter: TimeFilter): string | null {
   if (filter === 'all-time') {
      return null;
   }
   const end = new Date();
   const start = new Date(end);
   if (filter === 'today') {
      start.setDate(end.getDate() - 1);
   } else if (filter === 'week') {
      start.setDate(end.getDate() - 7);
   } else {
      start.setMonth(end.getMonth() - 1);
   }
   try {
      const format = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
         month: 'short',
         day: 'numeric',
      });
      return `${format.format(start)} – ${format.format(end)}`;
   } catch {
      return null;
   }
}

function BoardColumnHeaders() {
   return (
      <div className={cn('hidden border-b border-border bg-gray-100 px-4 py-2', BOARD_GRID_CLASS)}>
         <span className={COLUMN_LABEL_ON_FILL_CLASS}>{_js('Rank')}</span>
         <span className={COLUMN_LABEL_ON_FILL_CLASS}>{_js('User')}</span>
         <span className={COLUMN_LABEL_ON_FILL_CLASS}>{_js('Recent Badges')}</span>
         <span className={cn('text-right', COLUMN_LABEL_ON_FILL_CLASS)}>{_js('Reputation')}</span>
      </div>
   );
}

/** Podium tints for ranks 1–3: the badge palette's quiet 100/200 fills
 *  in medal metals, not a ribbon graphic. */
const TOP_RANK_CLASSES = [
   'border-amber-300 bg-amber-100 text-amber-900',
   'border-gray-300 bg-gray-200 text-gray-900',
   'border-orange-300 bg-orange-100 text-orange-900',
];

function BoardRow({ row, rank }: { row: LeaderboardRow; rank: number }) {
   const topRankClass = TOP_RANK_CLASSES[rank - 1];
   return (
      <li
         className={cn(
            'relative flex items-center gap-3 px-4 py-2 transition-colors hover:bg-gray-50',
            BOARD_GRID_CLASS
         )}
      >
         {topRankClass ? (
            <span
               className={cn(
                  'flex size-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold tabular-nums',
                  topRankClass
               )}
            >
               {rank}
            </span>
         ) : (
            <span className="w-7 shrink-0 text-center text-sm tabular-nums text-gray-600">
               {rank}
            </span>
         )}
         <span className="flex min-w-0 flex-1 items-center gap-2.5 md:flex-none">
            <img
               src={row.avatar}
               alt=""
               loading={rank > 12 ? 'lazy' : undefined}
               className="my-0 size-8 shrink-0 rounded-full bg-gray-100 object-cover"
            />
            {/* Stretched over the whole row; the badge links below sit
                above it on their own z. */}
            <a
               href={row.url}
               className="truncate font-medium text-gray-900 after:absolute after:inset-0 hover:text-brand-500 hover:no-underline"
            >
               {row.name}
            </a>
         </span>
         <span className="hidden items-center gap-1.5 md:flex">
            {row.badges.map(badge => (
               <RecentBadge key={badge.url + badge.name} badge={badge} />
            ))}
         </span>
         <span className="text-right text-sm font-medium tabular-nums text-gray-900">
            <span aria-hidden="true">{formatNumber(row.reputation)}</span>
            <span className="sr-only">
               {/* Translators: %1 -> reputation points; */}
               {_js('%1 reputation', formatNumber(row.reputation))}
            </span>
         </span>
      </li>
   );
}

/**
 * One hexagon from the member's latest badges. The SVG comes prerendered
 * from the PHP badge library (the same markup the badge pages use); the
 * tooltip replaces v1's hand-rolled CSS hover card.
 */
function RecentBadge({ badge }: { badge: LeaderboardBadge }) {
   return (
      <Tooltip>
         <TooltipTrigger asChild>
            <a
               href={badge.url}
               aria-label={badge.name}
               className="relative z-[1] block rounded-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_svg]:block"
               dangerouslySetInnerHTML={{ __html: badge.iconSvg }}
            />
         </TooltipTrigger>
         <TooltipContent className="max-w-60">
            <p className="my-0 font-medium">{badge.name}</p>
            <p className="my-0">{badge.description}</p>
            <p className="my-0 text-gray-300">
               {/* Translators: %1 -> badge count; */}
               {___p(badge.count, 'Earned %1 time', 'Earned %1 times', formatNumber(badge.count))}
            </p>
         </TooltipContent>
      </Tooltip>
   );
}

/** Placeholder rows mirroring BoardRow's anatomy — rank, avatar, name,
 *  badge slots, reputation — on the same grid, so the real rows land
 *  without a reflow. */
function BoardSkeleton() {
   return (
      <div className="divide-y divide-gray-200">
         {Array.from({ length: 10 }, (_, index) => (
            <div key={index} className={cn('flex items-center gap-3 px-4 py-2', BOARD_GRID_CLASS)}>
               <Skeleton className="size-7 rounded-full" />
               <div className="flex flex-1 items-center gap-2.5 md:flex-none">
                  <Skeleton className="size-8 rounded-full" />
                  <Skeleton className="h-4 w-36" />
               </div>
               <Skeleton className="hidden h-7 w-32 md:block" />
               <Skeleton className="h-4 w-14 justify-self-end" />
            </div>
         ))}
      </div>
   );
}

/**
 * Numbered pagination under the board: URL-addressable pages with
 * prev/next and a windowed page range. The server caps the ranking at
 * ten pages, so the full range is always walkable. The current page
 * comes from the view state, not the payload: keepPreviousData keeps
 * pageData a page behind for the whole fetch, and a bar driven by it
 * would stop Next from advancing past a slow response.
 */
function BoardPagination({
   page,
   filter,
   pageData,
   onPageChange,
}: {
   page: number;
   filter: TimeFilter;
   pageData: LeaderboardPageData;
   onPageChange: (page: number) => void;
}) {
   const pageCount = Math.max(1, Math.ceil(pageData.total / pageData.perPage));
   if (pageCount <= 1) {
      return null;
   }
   const range = calculatePageRange(page, pageCount, 5);
   const pages = [];
   for (let i = range.start; i <= range.end; i++) {
      pages.push(i);
   }
   const goTo = (event: ReactMouseEvent, target: number) => {
      // Modified clicks keep the native link behavior (new tab/window)
      // via the real href.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
         return;
      }
      event.preventDefault();
      if (target >= 1 && target <= pageCount && target !== page) {
         onPageChange(target);
      }
   };
   // The href names the filter too: on a fresh load the URL has no
   // ?filter=, and a copied or new-tab link without one can resolve to
   // a different period via the server's adaptive default.
   const pageHref = (target: number) => {
      const url = new URL(window.location.href);
      url.searchParams.set('filter', filter);
      url.searchParams.set('page', String(target));
      return url.pathname + url.search;
   };
   return (
      <div className="mt-4">
         <Pagination>
            <PaginationContent className="list-none pl-0">
               <PaginationItem>
                  <PaginationPrevious
                     href={pageHref(page - 1)}
                     aria-disabled={page === 1}
                     className={cn('no-underline', page === 1 && 'pointer-events-none opacity-50')}
                     onClick={event => goTo(event, page - 1)}
                  />
               </PaginationItem>
               {pages.map(target => (
                  <PaginationItem key={target}>
                     <PaginationLink
                        href={pageHref(target)}
                        isActive={target === page}
                        className="no-underline"
                        onClick={event => goTo(event, target)}
                     >
                        {target}
                     </PaginationLink>
                  </PaginationItem>
               ))}
               <PaginationItem>
                  <PaginationNext
                     href={pageHref(page + 1)}
                     aria-disabled={page === pageCount}
                     className={cn(
                        'no-underline',
                        page === pageCount && 'pointer-events-none opacity-50'
                     )}
                     onClick={event => goTo(event, page + 1)}
                  />
               </PaginationItem>
            </PaginationContent>
         </Pagination>
      </div>
   );
}

// Group digits in the page's language, not the browser locale — the rest
// of the page is rendered in the view language.
function formatNumber(value: number): string {
   try {
      return value.toLocaleString(document.documentElement.lang || undefined);
   } catch {
      return String(value);
   }
}

interface BoardView {
   filter: TimeFilter;
   page: number;
}

/**
 * The view the board shows — time period and page — as one piece of
 * state mirrored into the ?filter= and ?page= params the old page used,
 * so old links keep working. One state means a filter switch resets the
 * page in the same update, so the query never fetches a mismatched
 * (new filter, old page) pair; one URL write per change means Back and
 * Forward restore both params together. The initial view comes from the
 * caller (the server's payload), not the URL; popstate re-parses the
 * URL because history entries carry views this hook itself wrote.
 */
function useBoardView(
   validFilters: TimeFilter[],
   initialView: BoardView
): [BoardView, (view: BoardView) => void] {
   const parse = (search: string): BoardView => {
      const params = new URLSearchParams(search);
      const rawFilter = params.get('filter');
      const rawPage = params.get('page');
      const parsedPage = rawPage ? Number.parseInt(rawPage, 10) : 1;
      return {
         filter:
            rawFilter !== null && validFilters.includes(rawFilter as TimeFilter)
               ? (rawFilter as TimeFilter)
               : initialView.filter,
         page: Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1,
      };
   };
   const [view, setView] = useState<BoardView>(initialView);
   useEffect(() => {
      const onPopState = () => setView(parse(window.location.search));
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState);
      // parse is captured at mount; its inputs are stable props.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
   const set = (next: BoardView) => {
      // Same view, no write: a repeat click during a slow fetch must not
      // bury the real previous page under duplicate history entries.
      if (next.filter === view.filter && next.page === view.page) {
         return;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('filter', next.filter);
      if (next.page <= 1) {
         url.searchParams.delete('page');
      } else {
         url.searchParams.set('page', String(next.page));
      }
      window.history.pushState(null, '', url.toString());
      setView(next);
   };
   return [view, set];
}
